import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Building2, Mail, MapPin, ExternalLink } from 'lucide-react';
import styles from './legal.module.css';
import livePulseLogo from '../assets/live-pulse-logo.png';

const CONTACT_EMAIL = 'livepulse@ottosoft.in';
const COMPANY_NAME = 'Ottosoft Private Limited';
const COMPANY_URL = 'https://ottosoft.in';
const APP_NAME = 'LivePulse';

const SECTIONS = [
    { id: 'what', num: '01', title: 'What is LivePulse' },
    { id: 'company', num: '02', title: 'Who makes LivePulse' },
    { id: 'how', num: '03', title: 'How it works' },
    { id: 'contact', num: '04', title: 'Get in touch' },
];

export default function AboutPage() {
    const navigate = useNavigate();

    return (
        <div className={styles.page}>
            {/* Top bar */}
            <div className={styles.topBar}>
                <div className={styles.topBarInner}>
                    <Link to="/" className={styles.logoRow}>
                        <img src={livePulseLogo} alt="LivePulse" className={styles.logoBrand} />
                    </Link>
                    <button type="button" className={styles.backBtn} onClick={() => navigate(-1)}>
                        <ArrowLeft size={14} />
                        Back
                    </button>
                </div>
            </div>

            {/* Hero */}
            <div className={styles.hero}>
                <div className={styles.heroMesh} aria-hidden />
                <div className={styles.heroInner}>
                    <span className={styles.heroLabel}>About</span>
                    <h1 className={styles.heroTitle}>About LivePulse</h1>
                    <div className={styles.heroBadges}>
                        <span className={styles.heroBadge}>
                            <Building2 size={12} />
                            Made by {COMPANY_NAME}
                        </span>
                    </div>
                </div>
            </div>

            {/* Body */}
            <div className={styles.body}>
                <nav className={styles.toc} aria-label="Table of contents">
                    <div className={styles.tocTitle}>Contents</div>
                    <ol className={styles.tocList}>
                        {SECTIONS.map((s) => (
                            <li key={s.id} className={styles.tocItem}>
                                <a href={`#${s.id}`}>
                                    <span className={styles.tocNum}>{s.num}</span>
                                    {s.title}
                                </a>
                            </li>
                        ))}
                    </ol>
                </nav>

                <section id="what" className={styles.section}>
                    <div className={styles.sectionHead}>
                        <span className={styles.sectionNum}>1</span>
                        <h2 className={styles.sectionTitle}>What is LivePulse</h2>
                    </div>
                    <div className={styles.sectionDivider} />
                    <div className={styles.sectionBody}>
                        <p>
                            {APP_NAME} is a live polling and audience engagement platform for events, classrooms,
                            and webinars. Hosts create real-time polls, quizzes, word clouds, and Q&amp;A sessions
                            from a dashboard, and participants respond instantly from their own phone or laptop —
                            no app to install, no account to create.
                        </p>
                        <p>
                            Participants join by entering a 6-digit code (or scanning a QR code) in any web
                            browser. The platform supports 25 activity types in total, from simple multiple-choice
                            polls to drawing prompts, budget-allocation exercises, and speed quizzes, so a single
                            host can run an entire session — voting, discussion, and feedback — without switching
                            tools.
                        </p>
                    </div>
                </section>

                <section id="company" className={styles.section}>
                    <div className={styles.sectionHead}>
                        <span className={styles.sectionNum}>2</span>
                        <h2 className={styles.sectionTitle}>Who makes LivePulse</h2>
                    </div>
                    <div className={styles.sectionDivider} />
                    <div className={styles.sectionBody}>
                        <p>
                            {APP_NAME} is built and operated by <strong>{COMPANY_NAME}</strong>
                            {' '}(
                            <a href={COMPANY_URL} target="_blank" rel="noopener noreferrer">
                                ottosoft.in <ExternalLink size={12} style={{ display: 'inline', verticalAlign: 'middle' }} />
                            </a>
                            ), a software company based in Patna, Bihar, India. Ottosoft owns the LivePulse product,
                            platform, and brand, and is responsible for its development, hosting, and support.
                        </p>
                    </div>
                </section>

                <section id="how" className={styles.section}>
                    <div className={styles.sectionHead}>
                        <span className={styles.sectionNum}>3</span>
                        <h2 className={styles.sectionTitle}>How it works</h2>
                    </div>
                    <div className={styles.sectionDivider} />
                    <div className={styles.sectionBody}>
                        <p>Running a session on {APP_NAME} follows four steps:</p>
                        <ul>
                            <li>Create an event in the dashboard and add one or more activities (polls, quizzes, Q&amp;A, and more)</li>
                            <li>Share the event's 6-digit code or join link with your audience</li>
                            <li>Launch activities when you're ready — moderate questions and control what's visible in real time</li>
                            <li>Watch results update live, then export or present them</li>
                        </ul>
                        <p>
                            Pricing is coin-based: a free Starter plan (10 demo coins, all activity types, unlimited
                            participants) covers getting started, with pay-as-you-go and unlimited monthly plans
                            available as usage grows. Full details are on the{' '}
                            <Link to="/#pricing">pricing section</Link> of the homepage.
                        </p>
                    </div>
                </section>

                <section id="contact" className={styles.section}>
                    <div className={styles.sectionHead}>
                        <span className={styles.sectionNum}>4</span>
                        <h2 className={styles.sectionTitle}>Get in touch</h2>
                    </div>
                    <div className={styles.sectionDivider} />
                    <div className={styles.sectionBody}>
                        <p>
                            Questions about {APP_NAME} or Ottosoft Private Limited? Reach out any time:
                        </p>
                        <div className={styles.contactBox}>
                            <p><strong>{COMPANY_NAME}</strong></p>
                            <p>
                                <Mail size={13} style={{ display: 'inline', marginRight: 6, verticalAlign: 'middle' }} />
                                Email: <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
                            </p>
                            <p>
                                <MapPin size={13} style={{ display: 'inline', marginRight: 6, verticalAlign: 'middle' }} />
                                Manik Chand Talab, behind Sangam Hotel, Anisabad, Patna, Bihar 800002
                            </p>
                        </div>
                    </div>
                </section>
            </div>

            {/* Footer */}
            <footer className={styles.footer}>
                <div className={styles.footerInner}>
                    <span className={styles.footerText}>© {new Date().getFullYear()} {COMPANY_NAME}. All rights reserved.</span>
                    <div className={styles.footerLinks}>
                        <Link to="/terms">Terms of Service</Link>
                        <Link to="/privacy">Privacy Policy</Link>
                        <Link to="/">Home</Link>
                    </div>
                </div>
            </footer>

            <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{
                    __html: JSON.stringify({
                        '@context': 'https://schema.org',
                        '@type': 'AboutPage',
                        name: `About ${APP_NAME}`,
                        url: 'https://livepulse.ottosoft.in/about',
                        mainEntity: {
                            '@type': 'Organization',
                            name: COMPANY_NAME,
                            url: COMPANY_URL,
                            email: CONTACT_EMAIL,
                            address: {
                                '@type': 'PostalAddress',
                                streetAddress: '1st Floor, Shekhar Complex, Khoja Imli Mazar',
                                addressLocality: 'Phulwarisarif',
                                addressRegion: 'Bihar',
                                postalCode: '801505',
                                addressCountry: 'IN',
                            },
                        },
                    }),
                }}
            />
        </div>
    );
}
